import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, X, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

const SESSION_HIDE_KEY = 'emb_founding_banner_dismissed_v1';

function useFoundingStatus(poll = false) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let stopped = false;
        async function fetchOnce() {
            try {
                const r = await api.foundingStatus();
                if (!stopped) setData(r);
            } catch {
                if (!stopped) setData(null);
            }
        }
        fetchOnce();
        // Optionally refresh every 60s so the counter feels alive without
        // hammering the backend. Only enabled on the marketing pill where
        // the "seats decreasing" urgency matters most.
        let t;
        if (poll) t = setInterval(fetchOnce, 60000);
        return () => { stopped = true; if (t) clearInterval(t); };
    }, [poll]);
    return data;
}

// ── Public-site floating corner pill ───────────────────────────────────
// Persistent, always visible, poll every 60s. Not dismissible — this is
// the primary conversion driver on marketing pages.
export function FoundingCounterPill() {
    const status = useFoundingStatus(true);
    if (!status?.active) return null;

    const left = status.slots_remaining ?? 0;
    const isUrgent = left <= 20;

    return (
        <Link
            to="/pricing#founding-banner"
            data-testid="founding-counter-pill"
            className={
                'fixed bottom-4 right-4 z-40 group flex items-center gap-2.5 rounded-full ' +
                'shadow-lg backdrop-blur-md pl-3 pr-4 py-2 border-2 transition-all ' +
                (isUrgent
                    ? 'bg-red-600 hover:bg-red-700 text-white border-red-700 animate-pulse'
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground border-primary')
            }
        >
            <div className={'h-7 w-7 rounded-full grid place-items-center flex-shrink-0 ' + (isUrgent ? 'bg-red-900/40' : 'bg-black/15')}>
                <Flame size={14} />
            </div>
            <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider font-semibold opacity-90 leading-none">
                    Founding 100
                </div>
                <div className="text-sm font-semibold leading-tight tabular-nums">
                    {left} {left === 1 ? 'seat' : 'seats'} left
                    <span className="opacity-80 font-normal"> · ₹249/mo lifetime</span>
                </div>
            </div>
            <ArrowRight size={14} className="opacity-80 group-hover:translate-x-0.5 transition-transform" />
        </Link>
    );
}

// ── In-app top banner ──────────────────────────────────────────────────
// Slim, dismissible (session-scoped), hidden for users who are already
// founding members. Shows the live counter and links to Pricing.
// Self-fetches billing status so the parent doesn't have to plumb it in.
export function InAppFoundingBanner({ billingStatus: billingProp }) {
    const status = useFoundingStatus(false);
    const [billingStatus, setBillingStatus] = useState(billingProp || null);
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem(SESSION_HIDE_KEY) === '1'; } catch { return false; }
    });

    useEffect(() => {
        if (billingProp) { setBillingStatus(billingProp); return; }
        let stopped = false;
        api.getBillingStatus()
            .then((s) => { if (!stopped) setBillingStatus(s); })
            .catch(() => { /* ignore */ });
        return () => { stopped = true; };
    }, [billingProp]);

    if (dismissed) return null;
    if (!status?.active) return null;
    if (billingStatus?.founding_member) return null;   // already claimed — no ask
    if (billingStatus?.unlimited_scans) return null;   // internal user — irrelevant

    const left = status.slots_remaining ?? 0;
    const isUrgent = left <= 20;

    function close() {
        try { sessionStorage.setItem(SESSION_HIDE_KEY, '1'); } catch { /* noop */ }
        setDismissed(true);
    }

    return (
        <div
            data-testid="founding-in-app-banner"
            className={
                'relative w-full border-b ' +
                (isUrgent
                    ? 'bg-red-600 text-white border-red-800'
                    : 'bg-gradient-to-r from-primary/95 via-primary to-primary/95 text-primary-foreground border-primary/40')
            }
        >
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
                <div className={'shrink-0 h-7 w-7 rounded-full grid place-items-center ' + (isUrgent ? 'bg-red-900/40' : 'bg-black/15')}>
                    <Sparkles size={14} />
                </div>
                <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">Founding 100 offer:</span>
                    <span className="opacity-90">Lock in <b>₹249/mo lifetime</b> (vs ₹399) — same 150 scans.</span>
                    <span className={'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs tabular-nums font-semibold ' + (isUrgent ? 'bg-white text-red-700' : 'bg-black/20')}>
                        <Flame size={11} /> {left} {left === 1 ? 'seat' : 'seats'} left
                    </span>
                </div>
                <Link
                    to="/pricing#founding-banner"
                    className={'shrink-0 inline-flex items-center gap-1 rounded-md px-3 py-1 text-xs font-semibold transition-colors ' +
                        (isUrgent ? 'bg-white text-red-700 hover:bg-red-50' : 'bg-white/20 hover:bg-white/30')}
                    data-testid="founding-in-app-cta"
                >
                    Claim my seat <ArrowRight size={12} />
                </Link>
                <button
                    onClick={close}
                    className="shrink-0 p-1 rounded-md opacity-70 hover:opacity-100 transition-opacity"
                    aria-label="Dismiss"
                    data-testid="founding-in-app-dismiss"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
